// Port of RecordRow (full) and RecordCardCompact (compact) from GroundTeam/Views/Components/RecordRow.swift: the card a
// store is shown as in lists, the dashboard, the map peek, territory and rep pages (full) and pipeline columns
// (compact). Presentational: the record item, its stats and the page's lookups come in as props; the rules live in
// storeRowModel.ts.
//
// The whole card is one link or button (accessible name = a one-line summary), so a keyboard user tabs once per store.
// Extra controls (map peek: Open / Assign / Change stage) go in `actions`, rendered outside the link, because
// interactive elements cannot nest.

import { BadgeCheck, Hand, MapPinned } from 'lucide-react';
import { memo, useMemo, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { RecordPayload } from '@/contracts/models';
import type { DataItem } from '@/data/hooks';
import type { RecordStats } from '@/domain/derived';
import { Badge, Chip, cardClass, cn } from '@/ui';
import { DaysSinceVisit } from './DaysSinceVisit';
import { OwnerAvatar } from './OwnerAvatar';
import { PriorityChip } from './PriorityChip';
import { RecordTypeBadge } from './RecordTypeBadge';
import { StageChip } from './StageChip';
import { storeRowModel, type StoreRowLookups, type StoreRowModel } from './storeRowModel';

export interface StoreRowProps {
  /** The decoded record with its row meta (`useVisibleRecords().items` / `.get(id)`). */
  item: DataItem<RecordPayload>;
  /** From `useStoreRowLookups()`, once per page. */
  lookups: StoreRowLookups;
  /** The record's derived stats when you already have them; defaults to `statsOf(lookups.stats, id)`. */
  stats?: RecordStats;
  /** `full` (RecordRow) or `compact` (RecordCardCompact, pipeline cards and peeks). */
  variant?: 'full' | 'compact';
  /** Makes the card a link (e.g. `/stores/${item.id}`). Takes precedence over `onClick`. */
  href?: string;
  /** Makes the card a button (select on the map, open a side panel). */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /** Highlights the card (selected pin, multi-select); when set, a button card reports it as pressed. */
  selected?: boolean;
  /** Stage chip; default on for `full`, off for `compact` (a pipeline column already says the stage). */
  showStage?: boolean;
  /** Controls under the card, outside the link (map peek buttons). */
  actions?: ReactNode;
  className?: string;
}

function StoreRowImpl({
  item,
  lookups,
  stats,
  variant = 'full',
  href,
  onClick,
  selected,
  showStage,
  actions,
  className,
}: StoreRowProps) {
  const m = useMemo(() => storeRowModel(item, lookups, stats), [item, lookups, stats]);
  const compact = variant === 'compact';
  const withStage = showStage ?? !compact;
  const interactive = !!href || !!onClick;

  const body = compact ? <CompactBody m={m} showStage={withStage} /> : <FullBody m={m} showStage={withStage} />;
  const inner = cn('flex w-full min-w-0 flex-col text-left', compact ? 'gap-2 p-3' : 'gap-3 p-4', 'rounded-card');

  let content: ReactNode;
  if (href) {
    content = (
      <Link to={href} onClick={onClick} aria-label={m.ariaLabel} aria-current={selected ? 'true' : undefined} className={inner}>
        {body}
      </Link>
    );
  } else if (onClick) {
    content = (
      <button type="button" onClick={onClick} aria-label={m.ariaLabel} aria-pressed={selected} className={inner}>
        {body}
      </button>
    );
  } else {
    content = (
      <div aria-label={m.ariaLabel} role="group" className={inner}>
        {body}
      </div>
    );
  }

  return (
    <article
      className={cn(
        cardClass,
        'relative min-w-0',
        interactive && 'transition-[box-shadow,transform] duration-150 hover:shadow-raised active:scale-[0.995]',
        selected && 'ring-2 ring-primary',
        m.archived && 'opacity-80',
        className,
      )}
    >
      {content}
      {actions != null && <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3">{actions}</div>}
    </article>
  );
}

/** Memoized on (item, lookups, stats): a row re-renders only when its record or the page's lookups change. */
export const StoreRow = memo(StoreRowImpl);

/** "LEAD ✋ Newer app Archived" above the name. */
function TypeLine({ m }: { m: StoreRowModel }) {
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      <RecordTypeBadge type={m.type} variant="label" />
      {m.doNotContact && (
        <span role="img" aria-label="Do not contact" title="Do not contact" className="inline-flex text-danger">
          <Hand aria-hidden className="size-3" strokeWidth={2.6} />
        </span>
      )}
      {m.readOnly && (
        <Badge tone="warning" size="xs" title="Created by a newer version of Ground Team. Read-only here until the console is updated.">
          Newer app
        </Badge>
      )}
      {m.archived && (
        <Badge tone="neutral" size="xs">
          Archived
        </Badge>
      )}
    </span>
  );
}

function FullBody({ m, showStage }: { m: StoreRowModel; showStage: boolean }) {
  return (
    <>
      <span className="flex min-w-0 items-start gap-3">
        <RecordTypeBadge type={m.type} size={40} label={null} />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <TypeLine m={m} />
          <span className="line-clamp-2 text-[15px] leading-snug font-semibold text-fg">{m.name}</span>
          {m.subtitle !== '' && <span className="line-clamp-2 text-sm text-fg-muted">{m.subtitle}</span>}
          {m.contactLine !== '' && <span className="truncate text-xs text-fg-muted">{m.contactLine}</span>}
          {m.cardFields !== '' && <span className="line-clamp-2 text-xs font-medium text-accent">{m.cardFields}</span>}
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1.5">
          <PriorityChip priority={m.priority} />
          {showStage && <StageChip stage={m.stage} />}
        </span>
      </span>
      <span className="flex min-w-0 items-center gap-2">
        <DaysSinceVisit recency={m.visit} />
        <span className="flex-1" />
        {m.lastActivityText && <span className="min-w-0 truncate text-right text-xs text-fg-muted">{m.lastActivityText}</span>}
      </span>
      <span className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
        <span className="inline-flex items-center gap-1 text-[11px] text-fg-muted">
          <MapPinned aria-hidden className="size-3" />
          {m.visitCountText}
        </span>
        {m.verifiedCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-success-text">
            <BadgeCheck aria-hidden className="size-3" />
            {m.verifiedCount} verified
          </span>
        )}
        {m.valueText && <span className="text-[11px] font-semibold text-accent tabular-nums">{m.valueText}</span>}
        <span className="flex-1" />
        {m.owner && <OwnerAvatar user={m.owner} presence={m.ownerPresence} size="xs" role="Owner" />}
      </span>
    </>
  );
}

function CompactBody({ m, showStage }: { m: StoreRowModel; showStage: boolean }) {
  return (
    <>
      <span className="flex min-w-0 items-start gap-2.5">
        <RecordTypeBadge type={m.type} size={32} label={null} />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <TypeLine m={m} />
          <span className="line-clamp-2 text-sm leading-snug font-semibold text-fg">{m.name}</span>
          {m.subtitle !== '' && <span className="truncate text-xs text-fg-muted">{m.subtitle}</span>}
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          <PriorityChip priority={m.priority} size="xs" />
          {showStage && <StageChip stage={m.stage} size="xs" />}
          {m.lastVisitResult && (
            <Chip color={m.lastVisitResult.colorHex} filled size="xs" title={`Last visit result: ${m.lastVisitResult.name}`}>
              <span className="sr-only">Last visit result </span>
              {m.lastVisitResult.name}
            </Chip>
          )}
        </span>
      </span>
      {m.cardFields !== '' && <span className="truncate text-[11px] font-medium text-accent">{m.cardFields}</span>}
      <span className="flex min-w-0 items-center gap-2 text-xs">
        <DaysSinceVisit recency={m.visit} format="short" />
        {m.valueText && <span className="font-semibold text-accent tabular-nums">{m.valueText}</span>}
        <span className="flex-1" />
        {m.owner && <OwnerAvatar user={m.owner} presence={m.ownerPresence} size="xs" role="Owner" />}
      </span>
    </>
  );
}
